import avatarPng from '@/../img/avatar.png';
import { authorizedPlacesList } from '@/user/user-selectors';
import React from 'react';
import { FaCog, FaFileContract, FaSignOutAlt } from 'react-icons/fa';
import { MdExplore } from 'react-icons/md';
import { useSelector } from 'react-redux';
import { Dropdown, Popover, Tooltip, Whisper } from 'rsuite';
import styled from 'styled-components';
import { NavItemSectionWrapper } from './nav-item-styles';

export const avatarAttrs = {
  alt: 'Avatar',
  'data-cy': 'user-avatar',
  'data-testid': 'user-avatar',
};

const AvatarImg = styled.img.attrs(({ src }) => ({
  ...avatarAttrs,
  src: src ?? avatarPng,
}))`
  border-radius: 50%;
  max-width: 70px;
  max-height: 32px;
  margin: auto;
  cursor: pointer;
`;

const InnerTriggerWrapper = styled.div`
  margin-bottom: 38px;
`;

const MIN_CITIES_REQUIRED_FOR_SWITCHER = 2;

interface MenuProps {
  className?: string;
  style?: React.CSSProperties;
  placement?: string;
  onClose: () => void;
  cityName?: string;
  onProfileClick: () => void;
  onLogoutClick: () => void;
  onLegalClick: () => void;
  onChangeCityClick: () => void;
}

// Menu was placed in a popover to ensure it is always on top when toggled
const Menu = React.forwardRef<HTMLDivElement, MenuProps>((props, ref) => {
  const { onProfileClick, onLogoutClick, onLegalClick, onChangeCityClick } =
    props;

  const handleSelect = (callback: () => void) => {
    callback();
    props.onClose();
  };

  const userAuthorizedCities = useSelector(authorizedPlacesList);

  return (
    <Popover ref={ref} className={props.className} style={props.style} full>
      <Dropdown.Menu>
        <Dropdown.Item
          data-cy="profile-button"
          onSelect={() => handleSelect(onProfileClick)}
          icon={<FaCog />}
        >
          Profile
        </Dropdown.Item>

        {userAuthorizedCities?.length >= MIN_CITIES_REQUIRED_FOR_SWITCHER && (
          <Dropdown.Item
            data-cy="change-city-button"
            onSelect={() => handleSelect(onChangeCityClick)}
            icon={<MdExplore />}
          >
            Change City
          </Dropdown.Item>
        )}

        <Dropdown.Item
          data-cy="legal-button"
          onSelect={() => handleSelect(onLegalClick)}
          icon={<FaFileContract />}
        >
          Legal
        </Dropdown.Item>

        <Dropdown.Item
          data-cy="logout-button"
          onSelect={() => handleSelect(onLogoutClick)}
          icon={<FaSignOutAlt />}
        >
          Logout
        </Dropdown.Item>
      </Dropdown.Menu>
    </Popover>
  );
});

Menu.displayName = 'Menu';

interface AvatarProps {
  cityName: string;
  onLegalClick: () => void;
  onProfileClick: () => void;
  onLogoutClick: () => void;
  onChangeCityClick: () => void;
  src?: string;
}

export function Avatar(props: AvatarProps) {
  return (
    <>
      <Whisper
        trigger={['click']}
        placement="autoHorizontalEnd"
        speaker={(speakerProps, ref) => {
          return (
            <Menu
              cityName={props.cityName}
              onLegalClick={props.onLegalClick}
              onProfileClick={props.onProfileClick}
              onLogoutClick={props.onLogoutClick}
              onChangeCityClick={props.onChangeCityClick}
              {...speakerProps}
              ref={ref}
            />
          );
        }}
      >
        <InnerTriggerWrapper>
          <Whisper
            trigger={['hover']}
            placement="right"
            speaker={(_, ref) => {
              return (
                <Tooltip visible ref={ref}>
                  {props.cityName}
                </Tooltip>
              );
            }}
          >
            <NavItemSectionWrapper>
              <AvatarImg src={props.src} />
            </NavItemSectionWrapper>
          </Whisper>
        </InnerTriggerWrapper>
      </Whisper>
    </>
  );
}
